import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { useAppSelector } from "../../../framework/customStore";
import { GeofenceShapeType, GeofenceShapeTypeName } from "../../../models/geofence/geofence";
import { GeofenceSaveDialog } from "./geofenceSaveDialog";

const shapeTypeToGoogleMode = (
    mode: GeofenceShapeTypeName
): google.maps.drawing.OverlayType => {
    switch (mode) {
        case "Circle":
            return google.maps.drawing.OverlayType.CIRCLE;
        case "Rectangle":
            return google.maps.drawing.OverlayType.RECTANGLE;
        case "Polygon":
        default:
            return google.maps.drawing.OverlayType.POLYGON;
    }
};

const extractShapeData = (
    type: GeofenceShapeTypeName,
    overlay: google.maps.Circle | google.maps.Rectangle | google.maps.Polygon
): string => {
    if (type === "Circle") {
        const circle = overlay as google.maps.Circle;
        const center = circle.getCenter();
        return JSON.stringify({
            centerLat: center.lat(),
            centerLng: center.lng(),
            radiusMeters: circle.getRadius(),
        });
    }
    if (type === "Rectangle") {
        const rect = overlay as google.maps.Rectangle;
        const bounds = rect.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        return JSON.stringify({
            northEastLat: ne.lat(),
            northEastLng: ne.lng(),
            southWestLat: sw.lat(),
            southWestLng: sw.lng(),
        });
    }
    // Polygon
    const polygon = overlay as google.maps.Polygon;
    const path = polygon.getPath();
    const points: { lat: number; lng: number }[] = [];
    path.forEach((latLng) => points.push({ lat: latLng.lat(), lng: latLng.lng() }));
    return JSON.stringify({ points });
};

export const GeofenceDrawing = () => {
    const map = useMap();
    const drawingMode = useAppSelector((s) => s.geofence.drawingMode);
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
    const currentOverlayRef = useRef<google.maps.MVCObject | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingShapeType, setPendingShapeType] = useState<GeofenceShapeType>(GeofenceShapeType.Circle);
    const [pendingShapeData, setPendingShapeData] = useState<string>("");

    useEffect(() => {
        if (!map) return;

        const dm = new google.maps.drawing.DrawingManager({
            drawingControl: false,
            circleOptions: { editable: true, draggable: true, fillOpacity: 0.15 },
            rectangleOptions: { editable: true, draggable: true, fillOpacity: 0.15 },
            polygonOptions: { editable: true, draggable: true, fillOpacity: 0.15 },
        });
        dm.setMap(map);
        drawingManagerRef.current = dm;

        const listener = dm.addListener(
            "overlaycomplete",
            (event: google.maps.drawing.OverlayCompleteEvent) => {
                dm.setDrawingMode(null);

                let modeName: GeofenceShapeTypeName;
                switch (event.type) {
                    case google.maps.drawing.OverlayType.CIRCLE:
                        modeName = "Circle";
                        break;
                    case google.maps.drawing.OverlayType.RECTANGLE:
                        modeName = "Rectangle";
                        break;
                    case google.maps.drawing.OverlayType.POLYGON:
                        modeName = "Polygon";
                        break;
                    default:
                        return;
                }

                const shapeData = extractShapeData(modeName, event.overlay as any);
                currentOverlayRef.current = event.overlay;

                setPendingShapeType(GeofenceShapeType[modeName]);
                setPendingShapeData(shapeData);
                setDialogOpen(true);
            }
        );

        return () => {
            google.maps.event.removeListener(listener);
            if (currentOverlayRef.current) {
                (currentOverlayRef.current as any).setMap(null);
                currentOverlayRef.current = null;
            }
            dm.setMap(null);
            drawingManagerRef.current = null;
        };
    }, [map]);

    // Sync drawing mode with DrawingManager
    useEffect(() => {
        if (!drawingManagerRef.current) return;
        if (drawingMode) {
            drawingManagerRef.current.setDrawingMode(shapeTypeToGoogleMode(drawingMode));
        } else {
            drawingManagerRef.current.setDrawingMode(null);
        }
    }, [drawingMode]);

    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
        // Remove the temporary drawn overlay
        if (currentOverlayRef.current) {
            (currentOverlayRef.current as any).setMap(null);
            currentOverlayRef.current = null;
        }
    }, []);

    if (!dialogOpen) return null;

    return (
        <GeofenceSaveDialog
            open={dialogOpen}
            shapeType={pendingShapeType}
            shapeData={pendingShapeData}
            onClose={handleDialogClose}
        />
    );
};
